import { clsx } from '@alexis/helpers/clsx';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import React, {
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import Switch from '@components/Switch';

import {
  DIRECT_ONLY_PARAM,
  FARE_CALENDAR_CORE_CONFIG_KEY,
  SHOW_FLY_TO_ANYWHERE,
} from '@constants/flight';

import { GlobalContext } from '@context/GlobalContext';

import ArrowIcon from '@icons/arrow_short.svg';

import {
  getCurrencyState,
  getIsRtlState,
  getTranslationsState,
  getUserState,
} from '@redux/selectors';

import type { FlightSearchType } from '@wegoTypes/flights/flightSearchType';

import styles from './FlightDateSelector.module.scss';

export interface FlightDateSelectorProps {
  clientId: string | undefined;
  clientSessionId: string | undefined;
  className?: string;
  fromHasError?: boolean;
  fromMillisecond?: number;
  fromValue?: string;
  isFromFocus: boolean;
  isFromValidated: boolean;
  isToFocus?: boolean;
  isToValidated?: boolean;
  limitFromMilliseconds?: number;
  limitTillMilliseconds: number;
  onDateChanged(value: Date, field: 'inbound' | 'outbound'): void;
  onDateReset(): void;
  onFromFocus(): void;
  onToFocus?(): void;
  onAnytimeClick(field: 'inbound' | 'outbound'): void;
  onFromDateKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void;
  onToDateKeyDown?(event: React.KeyboardEvent<HTMLDivElement>): void;
  onRemoveReturnDateClick(): void;
  pageViewId: string;
  fromInputValue: string;
  setFromInputValue: React.Dispatch<React.SetStateAction<string>>;
  toInputValue: string;
  setToInputValue: React.Dispatch<React.SetStateAction<string>>;
  searchType: FlightSearchType;
  style?: React.CSSProperties;
  toHasError?: boolean;
  toMillisecond?: number;
  toValue?: string;
  isShowFareCalendarNote: boolean;
  arrival?: InboundPlace;
  departure?: OutboundPlace;
  isShowPriceForRoundTrip?: boolean;
}

const FlightDateSelector: React.FC<PropsWithChildren<FlightDateSelectorProps>> = ({
  clientId,
  clientSessionId,
  children,
  className,
  fromHasError,
  fromMillisecond,
  fromValue,
  isFromFocus,
  isFromValidated,
  searchType,
  isToFocus,
  isToValidated,
  limitFromMilliseconds,
  limitTillMilliseconds,
  onDateChanged,
  onDateReset,
  onFromFocus,
  onToFocus,
  onFromDateKeyDown,
  onToDateKeyDown,
  onAnytimeClick,
  pageViewId,
  style,
  toHasError,
  toMillisecond,
  toValue,
  fromInputValue,
  setFromInputValue,
  toInputValue,
  setToInputValue,
  isShowFareCalendarNote,
  arrival,
  departure,
  isShowPriceForRoundTrip,
}) => {
  const user = useSelector(getUserState);
  const translations = useSelector(getTranslationsState);
  const isRtl = useSelector(getIsRtlState);
  const currency = useSelector(getCurrencyState);

  const containerDivRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const { userCentricCoreConfigs } = useContext(GlobalContext);

  const fareCalendarCoreConfig = userCentricCoreConfigs.find(
    (coreConfig) => coreConfig.key === FARE_CALENDAR_CORE_CONFIG_KEY,
  );

  const isShowFareCalendarVariantA = fareCalendarCoreConfig?.value === '1';

  const isOneWay = searchType === 'oneWay';
  const isRange = searchType === 'roundTrip';

  const [searchParams] = useSearchParams();
  const { state, pathname } = useLocation();

  const isDirectFlightOnly = searchParams.has(DIRECT_ONLY_PARAM);

  const handleFromInputContainerClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      if (isFromFocus) {
        event.stopPropagation();
      }
      onFromFocus();
    },
    [isFromFocus, onFromFocus],
  );

  const handleToInputContainerClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      if (isFromFocus || isToFocus) {
        event.stopPropagation();
      }

      onToFocus?.();
    },
    [isFromFocus, isToFocus, onToFocus],
  );

  const isValid = useCallback(
    (isNext: boolean, isInboundDate?: boolean): boolean => {
      const fromMillisecondToCompare = fromMillisecond || new Date().setHours(0, 0, 0, 0);

      // Inbound date valid check
      if (isInboundDate) {
        if (toMillisecond) {
          if (isNext) {
            return toMillisecond < limitTillMilliseconds;
          }

          return fromMillisecondToCompare < toMillisecond;
        }

        return false;
      }

      // Outbound date valid check
      if (isNext) {
        if (toMillisecond) {
          return fromMillisecondToCompare < toMillisecond;
        }

        return fromMillisecondToCompare < limitTillMilliseconds;
      }

      return fromMillisecondToCompare > (limitFromMilliseconds || new Date().setHours(0, 0, 0, 0));
    },
    [fromMillisecond, limitFromMilliseconds, limitTillMilliseconds, toMillisecond],
  );

  const handleChevronClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>, isNext: boolean, isInboundDate?: boolean) => {
      e.stopPropagation();

      if (!isValid(isNext, isInboundDate)) {
        return;
      }

      if (isNext) {
        const nextDate = new Date(isInboundDate ? toMillisecond! : fromMillisecond!);
        nextDate.setDate(nextDate.getDate() + 1);

        onDateChanged(nextDate, isInboundDate ? 'inbound' : 'outbound');
      } else {
        const previousDate = new Date(isInboundDate ? toMillisecond! : fromMillisecond!);
        previousDate.setDate(previousDate.getDate() - 1);

        onDateChanged(previousDate, isInboundDate ? 'inbound' : 'outbound');
      }
    },
    [fromMillisecond, isValid, onDateChanged, toMillisecond],
  );

  const handleDateReset: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.stopPropagation();

      if (clientId && clientSessionId) {
        const eventData = {
          id: pageViewId,
          category: 'search_form',
          object: isOneWay ? 'oneway' : isRange ? 'roundtrip' : 'multicity',
          action: 'clear_calendar',
          value: '',
        };

        genzoTrackActionEvent(API_BASE_URL, clientId, clientSessionId, user?.userHash, eventData);
      }

      onDateReset();
      onFromFocus();
    },
    [clientId, clientSessionId, onDateReset, onFromFocus, pageViewId, user?.userHash],
  );

  const handleDirectFlightToggle = useCallback(() => {
    if (isDirectFlightOnly) {
      searchParams.delete(DIRECT_ONLY_PARAM);
    } else {
      searchParams.set(DIRECT_ONLY_PARAM, 'true');
    }

    if (clientId && clientSessionId) {
      const eventData = {
        id: pageViewId,
        category: 'search_form',
        object: 'fare_calendar',
        action: 'direct_flights_prices',
        value: !isDirectFlightOnly ? 'TRUE' : 'FALSE',
      };

      genzoTrackActionEvent(API_BASE_URL, clientId, clientSessionId, user?.userHash, eventData);
    }

    navigate({ pathname, search: searchParams.toString() }, { replace: true, state });
  }, [
    clientId,
    clientSessionId,
    isDirectFlightOnly,
    pageViewId,
    pathname,
    searchParams,
    state,
    user?.userHash,
  ]);

  //Align the fare calendar to the bottom of the page when it is not visible
  useEffect(() => {
    if (!(isFromFocus || isToFocus) && containerDivRef.current) {
      containerDivRef.current.style.removeProperty('top');
    } else {
      const timeout = setTimeout(() => {
        if (containerDivRef.current) {
          const rect = containerDivRef.current.getBoundingClientRect();

          if (
            containerDivRef.current &&
            !containerDivRef.current.style.top &&
            rect.bottom > window.innerHeight
          ) {
            const heightOfBottomBar = window.innerHeight - rect.bottom - 16;
            containerDivRef.current.style.top = `${heightOfBottomBar}px`;
          } else if (!containerDivRef.current.style.top) {
            containerDivRef.current.style.top = '-16px';
          }
        }
      }, 100);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isFromFocus, isToFocus, isShowFareCalendarNote]);

  // Generate the round trip note content
  const roundTripNote = useMemo(() => {
    if (
      !(
        searchType === 'roundTrip' &&
        departure?.code &&
        arrival &&
        arrival !== 'Anywhere' &&
        arrival.code
      )
    ) {
      return null;
    }

    const text = translations.fare_calendar_subtitle as string;
    const segments = text.split(/(\{0\}|\{1\})/);

    return segments.map((segment, index) => {
      if (segment === '{0}') {
        // Currency placeholder
        return (
          <span key={`currency-${index}`} className={styles.bold}>
            {currency.code}
          </span>
        );
      } else if (segment === '{1}') {
        // Route placeholder
        return (
          <span className={styles.bold} key={`route-${index}`}>
            ({isFromFocus ? departure.code : arrival.code}
            <ArrowIcon className={styles.arrowIcon} />
            {isFromFocus ? arrival.code : departure.code})
          </span>
        );
      } else {
        // Regular text
        return <span key={`text-${index}`}>{segment}</span>;
      }
    });
  }, [
    arrival,
    currency.code,
    departure?.code,
    isFromFocus,
    searchType,
    translations.fare_calendar_subtitle,
  ]);

  const showFlyToAnywhereCoreConfig = userCentricCoreConfigs.find(
    (coreConfig) => coreConfig.key === SHOW_FLY_TO_ANYWHERE,
  );
  const isVariantShowFlyToAnywhere = showFlyToAnywhereCoreConfig?.value === '1';

  const showAnytimeButton =
    (isFromFocus || isToFocus) &&
    arrival === 'Anywhere' &&
    isVariantShowFlyToAnywhere &&
    searchType !== 'multiCity';

  return (
    <div
      ref={containerDivRef}
      className={clsx(
        styles.container,
        className,
        (isFromFocus || isToFocus) && styles.focus,
        (isFromValidated || isToValidated) && styles.validated,
        !!(fromHasError || toHasError) && styles.error,
      )}
      style={style}
    >
      <div className={clsx(styles.inputContainers, isRange && styles.isRange)}>
        {(isFromFocus || isToFocus) && (
          <button
            disabled={!fromValue && !toValue}
            type='button'
            onClick={handleDateReset}
            className={styles.clear}
          >
            {translations.clear}
          </button>
        )}

        <div className={styles.dateInputsContainer}>
          <div
            className={clsx(
              styles.fromInputContainer,
              isFromFocus && styles.focus,
              isFromValidated && styles.validated,
              !!fromHasError && styles.error,
            )}
            data-testid='from-input-container'
            tabIndex={2}
          >
            <input
              data-testid='from-input-value'
              id='from-input-value'
              autoComplete='off'
              data-ai-preferred='true'
              data-ai-format='yyyy-mm-dd'
              aria-label='Departure'
              aria-describedby='date-picker-description'
              value={fromInputValue}
              className={styles.input}
              placeholder=' '
              onChange={(e) => {
                setFromInputValue(e.target.value);
              }}
              onKeyDown={onFromDateKeyDown}
              onClick={handleFromInputContainerClick}
            />
            <label htmlFor='from-input-value'>{translations.depart}</label>

            {showAnytimeButton && fromInputValue !== translations.anytime && (
              <div
                className={styles.anytimeButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onAnytimeClick?.('outbound');
                }}
              >
                {translations.anytime}
              </div>
            )}

            <div className={styles.leftAndRightChevronContainer}>
              <div
                className={clsx(
                  styles.chevronContainer,
                  styles.left,
                  (isFromFocus || isToFocus || !fromMillisecond) && styles.hide,
                )}
                data-testid='from-chevron-left'
                aria-controls='from-input-value'
                aria-label={isRtl ? 'Next depart date' : 'Previous depart date'}
                onClick={(e) => handleChevronClick(e, isRtl)}
              >
                <div
                  className={clsx(styles.chevron, styles.left, !isValid(isRtl) && styles.disabled)}
                />
              </div>

              <div
                className={clsx(
                  styles.chevronContainer,
                  styles.right,
                  (isFromFocus || isToFocus || !fromMillisecond) && styles.hide,
                )}
                data-testid='from-chevron-right'
                aria-controls='from-input-value'
                aria-label={isRtl ? 'Previous depart date' : 'Next depart date'}
                onClick={(e) => handleChevronClick(e, !isRtl)}
              >
                <div
                  className={clsx(
                    styles.chevron,
                    styles.right,
                    !isValid(!isRtl) && styles.disabled,
                  )}
                />
              </div>
            </div>
          </div>

          {isRange ? (
            <>
              <div className={styles.verticalLine} />

              <div
                className={clsx(
                  styles.toInputContainer,
                  isToFocus && styles.focus,
                  isToValidated && styles.validated,
                  !!toHasError && styles.error,
                )}
                data-testid='to-input-container'
                tabIndex={2}
              >
                <input
                  data-testid='to-input-value'
                  id='to-input-value'
                  autoComplete='off'
                  data-ai-preferred='true'
                  data-ai-format='yyyy-mm-dd'
                  aria-label='Return'
                  aria-describedby='date-picker-description'
                  value={toInputValue}
                  className={styles.input}
                  placeholder=' '
                  onChange={(e) => {
                    setToInputValue(e.target.value);
                  }}
                  onKeyDown={onToDateKeyDown}
                  onClick={handleToInputContainerClick}
                />
                <label htmlFor='to-input-value'>{translations.return}</label>

                <div className={styles.chevronAndRemoveReturnContainer}>
                  <div className={styles.leftAndRightChevronContainer}>
                    <div
                      className={clsx(
                        styles.chevronContainer,
                        styles.left,
                        (isFromFocus || isToFocus || !toMillisecond) && styles.hide,
                      )}
                      data-testid='to-chevron-left'
                      aria-controls='to-input-value'
                      aria-label={isRtl ? 'Next return date' : 'Previous return date'}
                      onClick={(e) => handleChevronClick(e, isRtl, true)}
                    >
                      <div
                        className={clsx(
                          styles.chevron,
                          styles.left,
                          !isValid(isRtl, true) && styles.disabled,
                        )}
                      />
                    </div>

                    <div
                      className={clsx(
                        styles.chevronContainer,
                        styles.right,
                        (isFromFocus || isToFocus || !toMillisecond) && styles.hide,
                      )}
                      data-testid='to-chevron-right'
                      aria-controls='to-input-value'
                      aria-label={isRtl ? 'Previous return date' : 'Next return date'}
                      onClick={(e) => handleChevronClick(e, !isRtl, true)}
                    >
                      <div
                        className={clsx(
                          styles.chevron,
                          styles.right,
                          !isValid(!isRtl, true) && styles.disabled,
                        )}
                      />
                    </div>
                  </div>
                </div>

                {showAnytimeButton && toInputValue !== translations.anytime && (
                  <div
                    className={styles.anytimeButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAnytimeClick?.('inbound');
                    }}
                  >
                    {translations.anytime}
                  </div>
                )}
              </div>
            </>
          ) : null}

          <div id='date-picker-description' className={styles.datePickerDescription}>
            Enter a date or use the arrow keys to change the current date.
          </div>
        </div>
      </div>

      {isShowFareCalendarNote && (isFromFocus || isToFocus) && (
        <div className={styles.fareCalendarNoteContainer}>
          <div className={styles.directFlightToggleContainer} onClick={(e) => e.stopPropagation()}>
            <Switch isRtl={isRtl} checked={isDirectFlightOnly} onClick={handleDirectFlightToggle} />
            {isShowFareCalendarVariantA
              ? translations.direct_flight_only
              : translations.fare_calendar_direct_flights}
          </div>

          {isShowPriceForRoundTrip && (
            <div
              key={`roundtrip-note-${isFromFocus ? 'from' : 'to'}`}
              className={clsx(
                styles.roundTripNote,
                isFromFocus && styles.fromFocus,
                isToFocus && styles.toFocus,
              )}
            >
              {roundTripNote}
            </div>
          )}

          {isShowFareCalendarVariantA && (
            <div className={styles.priceLegend}>
              <div className={styles.indicator}>
                <div className={styles.cheap}></div>
                <span>{translations.fare_calendar_cheap}</span>
              </div>
              <div className={styles.indicator}>
                <div className={styles.average}></div>
                <span>{translations.fare_calendar_average}</span>
              </div>
              <div className={styles.indicator}>
                <div className={styles.expensive}></div>
                <span>{translations.fare_calendar_expensive}</span>
              </div>
            </div>
          )}
        </div>
      )}

      <div
        className={clsx(styles.children, (isFromFocus || isToFocus) && styles.show)}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

export default FlightDateSelector;
